import React, { useEffect, useRef, useCallback, useState } from 'react'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfDay, isAfter } from 'date-fns'

interface DateRangePickerProps {
  id: string
  label?: string
  startDate: Date
  endDate: Date
  onStartDateChange: (date: Date) => void
  onEndDateChange: (date: Date) => void
  startTime: string // Format: "HH:MM"
  endTime: string // Format: "HH:MM"
  onStartTimeChange: (time: string) => void
  onEndTimeChange: (time: string) => void
  minWidth?: string
  disabled?: boolean
  transparency?: 'low' | 'medium' | 'high' | 'none'
  rounded?: boolean
  weekStartsOn?: 0 | 1 // 0 = Sunday, 1 = Monday (default)
}

export default function DateRangePicker({
  id,
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  minWidth = "min-w-[320px]",
  disabled = false,
  transparency = 'medium',
  rounded = true,
  weekStartsOn = 1
}: DateRangePickerProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(startDate)

  // Multi-day mode: if start and end dates are different, enable multi-day mode
  const [isMultiDay, setIsMultiDay] = useState(!isSameDay(startDate, endDate))
  const [selectingEndDate, setSelectingEndDate] = useState(false)

  // Update multi-day state when dates change externally
  useEffect(() => {
    if (!isSameDay(startDate, endDate)) {
      setIsMultiDay(true)
    }
  }, [startDate, endDate])

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
    setSelectingEndDate(false)
  }

  const handleDateClick = (date: Date) => {
    if (isMultiDay && selectingEndDate) {
      // Selecting end date
      if (date < startDate) {
        // If selected end is before start, swap them
        onEndDateChange(startDate)
        onStartDateChange(date)
      } else {
        onEndDateChange(date)
      }
      setSelectingEndDate(false)
    } else {
      // Selecting start date (or single day)
      onStartDateChange(date)
      if (!isMultiDay) {
        // Single day mode: set both dates to same day
        onEndDateChange(date)
      } else {
        // Multi-day mode: now select end date
        setSelectingEndDate(true)
        // If current end date is before new start, reset it
        if (endDate < date) {
          onEndDateChange(date)
        }
      }
    }
  }

  const handleMultiDayToggle = () => {
    const newIsMultiDay = !isMultiDay
    setIsMultiDay(newIsMultiDay)
    if (!newIsMultiDay) {
      // Switching to single day: set end date to start date
      onEndDateChange(startDate)
      setSelectingEndDate(false)
    } else {
      // Switching to multi-day: start selecting end date
      setSelectingEndDate(true)
    }
  }

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1))
  }

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onStartTimeChange(e.target.value)
  }

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onEndTimeChange(e.target.value)
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false)
      setSelectingEndDate(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  const getSelectedDisplay = () => {
    const startDisplay = format(startDate, 'MMM d, yyyy')
    const endDisplay = format(endDate, 'MMM d, yyyy')

    if (isSameDay(startDate, endDate)) {
      return `${startDisplay} ${startTime} - ${endTime}`
    }

    return `${startDisplay} ${startTime} - ${endDisplay} ${endTime}`
  }

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn })

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })
    const weeks = []

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    const today = startOfDay(new Date())

    return (
      <div className="p-4" onClick={(e) => {
        e.stopPropagation()
        // Don't preventDefault - it blocks checkbox and other form control interactions
      }}>
        {/* Header with month navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => handleMonthChange('prev')}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-sm font-medium text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <button
            type="button"
            onClick={() => handleMonthChange('next')}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Multi-day toggle */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`${id}-multi-day`}
              checked={isMultiDay}
              onChange={handleMultiDayToggle}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <label
              htmlFor={`${id}-multi-day`}
              className="ml-2 text-xs text-gray-600 cursor-pointer select-none"
            >
              Multi-day entry
            </label>
          </div>

          {isMultiDay && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              selectingEndDate
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {selectingEndDate ? 'Select end date' : 'Select start date'}
            </span>
          )}
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {(weekStartsOn === 0
            ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
          ).map(day => (
            <div key={day} className="text-xs text-gray-500 text-center py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day) => {
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth()
                const isDayToday = isSameDay(day, new Date())
                const isFutureDate = isAfter(startOfDay(day), today)
                const isStartDate = isSameDay(day, startDate)
                const isEndDate = isSameDay(day, endDate)
                const isInRange = isMultiDay && !isSameDay(startDate, endDate) &&
                  day >= startDate && day <= endDate
                const isRangeMiddle = isInRange && !isStartDate && !isEndDate

                return (
                  <button
                    type="button"
                    key={day.toISOString()}
                    onClick={() => {
                      if (isFutureDate) return
                      handleDateClick(day)
                    }}
                    disabled={isFutureDate}
                    className={`
                      w-8 h-8 text-xs rounded flex items-center justify-center transition-colors relative
                      ${isFutureDate
                        ? 'text-gray-300 cursor-not-allowed'
                        : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                      ${isDayToday && !isFutureDate && !isStartDate && !isEndDate ? 'ring-1 ring-blue-400' : ''}
                      ${isStartDate && !isFutureDate ? 'bg-blue-600 text-white font-semibold' : ''}
                      ${isEndDate && !isStartDate && !isFutureDate ? 'bg-green-600 text-white font-semibold' : ''}
                      ${isRangeMiddle && !isFutureDate ? 'bg-blue-100 text-blue-900' : ''}
                      ${!isInRange && !isDayToday && !isFutureDate && !isStartDate && !isEndDate ? 'hover:bg-gray-100' : ''}
                    `}
                  >
                    {format(day, 'd')}
                  </button>
                )
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Legend for multi-day */}
        {isMultiDay && !isSameDay(startDate, endDate) && (
          <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-600"></div>
              <span>Start</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-600"></div>
              <span>End</span>
            </div>
          </div>
        )}

        {/* Time inputs */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${id}-start-time`} className="block text-xs text-gray-500 mb-1">
                Start Time
              </label>
              <input
                type="time"
                id={`${id}-start-time`}
                value={startTime}
                onChange={handleStartTimeChange}
                className={`w-full px-2 py-1 text-xs border border-gray-300 ${rounded ? 'rounded' : ''} focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
            <div>
              <label htmlFor={`${id}-end-time`} className="block text-xs text-gray-500 mb-1">
                End Time
              </label>
              <input
                type="time"
                id={`${id}-end-time`}
                value={endTime}
                onChange={handleEndTimeChange}
                className={`w-full px-2 py-1 text-xs border border-gray-300 ${rounded ? 'rounded' : ''} focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div id={`${id}-filter`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <div
          className={`min-h-[42px] ${minWidth} border border-gray-300 ${rounded ? 'rounded-md' : ''} focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 p-2 relative bg-white ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleToggle()
            }
          }}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="text-sm text-gray-900 truncate block w-full pr-6">
            {getSelectedDisplay()}
          </span>

          {/* Dropdown arrow button */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <button
              type="button"
              onClick={handleToggle}
              disabled={disabled}
              data-testid="dropdown-toggle"
              className={`text-gray-400 hover:text-gray-600 focus:outline-none ${
                disabled ? 'cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Calendar Dropdown */}
        <div
          id={id}
          className={`${isOpen ? '' : 'hidden'} absolute z-[60] bottom-full mb-1 left-0 ${
            transparency === 'none'
              ? 'bg-white border border-gray-200'
              : transparency === 'low'
              ? 'bg-gradient-to-b from-white/90 to-white/85 backdrop-blur-sm border border-gray-200/70'
              : transparency === 'high'
              ? 'bg-gradient-to-b from-white/30 to-white/20 backdrop-blur-lg border border-gray-200/30'
              : 'bg-gradient-to-b from-white/70 to-white/60 backdrop-blur-md border border-gray-200/50'
          } ${rounded ? 'rounded-md' : ''} shadow-xl`}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          {renderCalendar()}
        </div>
      </div>
    </div>
  )
}
